import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useLocation } from "@/contexts/LocationContext";
import { useOrganization } from "@/contexts/OrganizationContext";

// Type definitions
type NPSPatientCall = {
  id: string;
  patientName: string;
  phone: string;
  locationId: string;
  locationName: string;
  lastVisitDate: Date;
  callStatus: 'Pending' | 'Completed' | 'No Answer';
  score: number | null;
  requestedReview: boolean;
  reviewLinkSent: boolean;
  reviewCompleted: boolean;
  reviewContent: string | null;
};

type GoogleReview = {
  id: string;
  patientId: string | null;
  patientName: string;
  locationId: string;
  rating: number;
  reviewText: string;
  reviewDate: Date;
  linkedToNPS: boolean;
  responseText: string | null;
  responseSent: boolean;
};

type NPSSummary = {
  overallScore: number;
  detractors: number;
  passives: number;
  promoters: number;
  totalResponses: number;
  byLocation: Record<string, number>;
  byTimeRange: Record<string, number>;
  callsCount: number;
  completedCount: number;
};

export default function NetPromoterScore() {
  const [timeRange, setTimeRange] = useState("last30days");
  const [activeTab, setActiveTab] = useState("overview");
  const { selectedLocationId, selectedLocationIds, locations, isLoadingLocations } = useLocation();
  const { currentOrganizationId } = useOrganization();

  // Get location name helper
  const getLocationName = (locationId: string) => {
    const location = locations.find(loc => loc.id === locationId);
    return location ? location.name : locationId;
  };

  // Get display title for current selection
  const getDisplayTitle = () => {
    if (selectedLocationIds.includes('all')) {
      return 'All Locations';
    } else if (selectedLocationIds.length === 1) {
      return getLocationName(selectedLocationIds[0]);
    } else {
      return `${selectedLocationIds.length} Locations Combined`;
    }
  };

  // Build API query parameters for multi-select
  const getAPIParams = () => {
    if (selectedLocationIds.includes('all')) {
      return '';
    } else if (selectedLocationIds.length === 1) {
      return `?locationId=${selectedLocationIds[0]}`;
    } else {
      // For multi-select, we'll fetch all and filter on frontend
      return '';
    }
  };

  // Get NPS Summary data
  const { data: npsSummary, isLoading: isLoadingSummary } = useQuery({
    queryKey: ['/api/nps/summary', selectedLocationIds, currentOrganizationId],
    enabled: !isLoadingLocations && selectedLocationIds.length > 0,
    queryFn: async () => {
      console.log('🔍 Fetching NPS summary for:', selectedLocationIds);
      
      if (selectedLocationIds.includes('all')) {
        const orgParam = currentOrganizationId ? `?organizationId=${currentOrganizationId}` : '';
        const response = await fetch(`/api/nps/summary${orgParam}`, { redirect: 'error' });
        if (!response.ok) throw new Error('Failed to fetch NPS summary');
        return await response.json() as NPSSummary;
      } else if (selectedLocationIds.length === 1) {
        const orgParam = currentOrganizationId ? `&organizationId=${currentOrganizationId}` : '';
        const response = await fetch(`/api/nps/summary?locationId=${selectedLocationIds[0]}${orgParam}`, { redirect: 'error' });
        if (!response.ok) throw new Error('Failed to fetch NPS summary');
        return await response.json() as NPSSummary;
      } else {
        // Multi-select: fetch all and aggregate
        const orgParam = currentOrganizationId ? `&organizationId=${currentOrganizationId}` : '';
        const responses = await Promise.all(
          selectedLocationIds.map(locationId => 
            fetch(`/api/nps/summary?locationId=${locationId}${orgParam}`, { redirect: 'error' }).then(res => res.json())
          )
        );
        
        // Aggregate the responses
        const aggregated: NPSSummary = {
          overallScore: 0,
          detractors: 0,
          passives: 0,
          promoters: 0,
          totalResponses: 0,
          byLocation: {},
          byTimeRange: responses[0]?.byTimeRange || {},
          callsCount: 0,
          completedCount: 0
        };
        
        responses.forEach(summary => {
          aggregated.detractors += summary.detractors;
          aggregated.passives += summary.passives;
          aggregated.promoters += summary.promoters;
          aggregated.totalResponses += summary.totalResponses;
          aggregated.callsCount += summary.callsCount;
          aggregated.completedCount += summary.completedCount;
          Object.assign(aggregated.byLocation, summary.byLocation);
        });
        
        // Recalculate overall score
        aggregated.overallScore = aggregated.totalResponses > 0
          ? Math.round(((aggregated.promoters - aggregated.detractors) / aggregated.totalResponses) * 100)
          : 0;
        
        return aggregated;
      }
    }
  });

  // Get NPS Patient Calls data
  const { data: patientCalls, isLoading: isLoadingCalls } = useQuery({
    queryKey: ['/api/nps/patient-calls', selectedLocationIds, currentOrganizationId],
    enabled: !isLoadingLocations && selectedLocationIds.length > 0,
    queryFn: async () => {
      console.log('🔍 Fetching NPS patient calls for organization:', currentOrganizationId);
      const orgParam = currentOrganizationId ? `?organizationId=${currentOrganizationId}` : '';
      const response = await fetch(`/api/nps/patient-calls${orgParam}`, { redirect: 'error' });
      if (!response.ok) {
        throw new Error('Failed to fetch NPS patient calls');
      }
      const allCalls = await response.json() as NPSPatientCall[];
      
      // Filter based on selection
      if (selectedLocationIds.includes('all')) {
        return allCalls;
      } else {
        return allCalls.filter(call => selectedLocationIds.includes(call.locationId));
      }
    }
  });

  // Get Google Reviews data
  const { data: googleReviews, isLoading: isLoadingReviews } = useQuery({
    queryKey: ['/api/google-reviews', selectedLocationIds, currentOrganizationId],
    enabled: !isLoadingLocations && selectedLocationIds.length > 0,
    queryFn: async () => {
      console.log('🔍 Fetching Google reviews for organization:', currentOrganizationId);
      let url = '/api/google-reviews';
      if (currentOrganizationId) {
        url += `?organizationId=${currentOrganizationId}`;
      }
      const response = await fetch(url, { redirect: 'error' });
      if (!response.ok) {
        throw new Error('Failed to fetch Google reviews');
      }
      const allReviews = await response.json() as GoogleReview[];
      
      // Filter based on selection
      if (selectedLocationIds.includes('all')) {
        return allReviews;
      } else {
        return allReviews.filter(review => selectedLocationIds.includes(review.locationId));
      }
    }
  });

  const isLoading = isLoadingSummary || isLoadingCalls || isLoadingReviews;
  
  // Calculate call target based on selected location (10 per location)
  const getCallTarget = () => {
    if (selectedLocationIds.includes('all')) {
      return 70; // 7 locations * 10 each
    } else {
      return selectedLocationIds.length * 10;
    }
  };

  // Derive summary from patientCalls if API returns empty totals
  const derivedFromCalls = React.useMemo(() => {
    if (!patientCalls || patientCalls.length === 0) return null;
    const scored = patientCalls.filter(c => c.score !== null && c.score !== undefined);
    if (scored.length === 0) return null;
    const detractors = scored.filter(c => (c.score as number) <= 6).length;
    const passives = scored.filter(c => (c.score as number) >= 7 && (c.score as number) <= 8).length;
    const promoters = scored.filter(c => (c.score as number) >= 9).length;
    const totalResponses = scored.length;
    const overallScore = Math.round(((promoters - detractors) / totalResponses) * 100);
    // byLocation averages
    const byLoc: Record<string, number> = {};
    const groups: Record<string, { total: number; count: number }> = {};
    for (const c of scored) {
      const id = c.locationId;
      if (!id) continue;
      const g = groups[id] || { total: 0, count: 0 };
      g.total += c.score as number;
      g.count += 1;
      groups[id] = g;
    }
    for (const [locId, g] of Object.entries(groups)) {
      const name = getLocationName(locId);
      byLoc[name] = Math.round(g.total / g.count);
    }
    return { detractors, passives, promoters, totalResponses, overallScore, byLocation: byLoc };
  }, [patientCalls, locations]);

  const effectiveSummary = React.useMemo(() => {
    if (npsSummary && npsSummary.totalResponses > 0) return npsSummary;
    if (derivedFromCalls) {
      return {
        overallScore: derivedFromCalls.overallScore,
        detractors: derivedFromCalls.detractors,
        passives: derivedFromCalls.passives,
        promoters: derivedFromCalls.promoters,
        totalResponses: derivedFromCalls.totalResponses,
        byLocation: derivedFromCalls.byLocation,
        byTimeRange: npsSummary?.byTimeRange || {},
        callsCount: npsSummary?.callsCount || 0,
        completedCount: npsSummary?.completedCount || 0
      } as NPSSummary;
    }
    return npsSummary as NPSSummary | undefined;
  }, [npsSummary, derivedFromCalls]);

  const npsBreakdownData = effectiveSummary ? [
    {
      range: "Detractors (0-6)",
      count: effectiveSummary.detractors,
      percentage: effectiveSummary.totalResponses > 0 ? (effectiveSummary.detractors / effectiveSummary.totalResponses) * 100 : 0,
      color: "#DC2626"
    },
    {
      range: "Passives (7-8)", 
      count: effectiveSummary.passives,
      percentage: effectiveSummary.totalResponses > 0 ? (effectiveSummary.passives / effectiveSummary.totalResponses) * 100 : 0,
      color: "#F59E0B"
    },
    {
      range: "Promoters (9-10)",
      count: effectiveSummary.promoters,
      percentage: effectiveSummary.totalResponses > 0 ? (effectiveSummary.promoters / effectiveSummary.totalResponses) * 100 : 0,
      color: "#10B981"
    }
  ] : [];

  const locationTrendData = (effectiveSummary && Object.keys(effectiveSummary.byLocation || {}).length > 0
    ? Object.entries(effectiveSummary.byLocation)
    : (derivedFromCalls ? Object.entries(derivedFromCalls.byLocation) : [])
  ).map(([name, score]) => ({
    name,
    score
  }));

  const timeTrendData = npsSummary ? Object.entries(npsSummary.byTimeRange).map(([month, score]) => ({
    month,
    score
  })) : [];

  function getNPSScoreClass(score: number): string {
    if (score >= 9) return "text-green-800 font-semibold";
    if (score >= 7) return "text-yellow-800 font-semibold";
    return "text-red-800 font-semibold";
  }

  function getScoreCategory(score: number): string {
    if (score >= 50) return "Excellent";
    if (score >= 30) return "Good";
    if (score >= 0) return "Needs Improvement";
    return "Poor";
  }

  function getColorForScore(score: number): string {
    if (score >= 50) return "bg-green-500";
    if (score >= 30) return "bg-yellow-500";
    return "bg-red-500";
  }

  return (
    <div className="container mx-auto py-6">
      <div className="flex flex-wrap gap-3 justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Net Promoter Score</h1>
          <p className="text-muted-foreground">
            {getDisplayTitle()}
            {selectedLocationIds.length > 1 && !selectedLocationIds.includes('all') && (
              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                Data Combined
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-4">
          <div>
            <Label htmlFor="timeRange" className="mb-1 block">Time Range</Label>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger id="timeRange" className="w-[180px]">
                <SelectValue placeholder="Select time range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last30days">Last 30 Days</SelectItem>
                <SelectItem value="last90days">Last 90 Days</SelectItem>
                <SelectItem value="last6months">Last 6 Months</SelectItem>
                <SelectItem value="lastYear">Last Year</SelectItem>
                <SelectItem value="allTime">All Time</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="responses">Individual Responses</TabsTrigger>
          <TabsTrigger value="callcampaign">Call Campaign</TabsTrigger>
          <TabsTrigger value="googlereviews">Google Reviews</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">NPS Score</CardTitle>
                <CardDescription>Current Net Promoter Score</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center h-32">
                  <div className="text-6xl font-bold mb-2">
                    {isLoading ? "-" : (effectiveSummary?.overallScore ?? 0)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {isLoading ? "Loading..." : getScoreCategory(effectiveSummary?.overallScore || 0)}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Response Distribution</CardTitle>
                <CardDescription>Breakdown by score category</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {npsBreakdownData.map((data) => (
                    <div key={data.range} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>{data.range}</span>
                        <span>{Math.round(data.percentage)}% ({data.count})</span>
                      </div>
                      <Progress value={data.percentage} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">NPS by Location</CardTitle>
                <CardDescription>Score comparison across locations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 pt-2">
                  {locationTrendData.map((item) => (
                    <div key={item.name} className="flex flex-wrap gap-3 justify-between items-center">
                      <span className="text-sm">{item.name}</span>
                      <div className="flex gap-2 items-center">
                        <span className={getNPSScoreClass(item.score)}>{item.score}</span>
                        <div className={`w-2 h-2 rounded-full ${getColorForScore(item.score)}`}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>NPS Recommendations</CardTitle>
              <CardDescription>Actions to improve your NPS score</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold">Address Common Detractor Feedback</h3>
                <ul className="list-disc list-inside space-y-2 text-sm">
                  <li>Reduce wait times during peak hours</li>
                  <li>Improve communication about appointment delays</li>
                  <li>Enhance staff training for consistent patient experience</li>
                  <li>Follow up with detractors to resolve their concerns</li>
                </ul>
              </div>
              <div className="space-y-4">
                <h3 className="font-semibold">Amplify Promoter Experiences</h3>
                <ul className="list-disc list-inside space-y-2 text-sm">
                  <li>Encourage satisfied patients to leave online reviews</li>
                  <li>Create a referral program for loyal patients</li>
                  <li>Highlight exceptional staff with recognition programs</li>
                  <li>Document success stories for marketing material</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends">
          <div className="grid grid-cols-1 gap-6">
            <Card className="col-span-1">
              <CardHeader>
                <CardTitle>NPS Over Time</CardTitle>
                <CardDescription>How your NPS score has changed over time</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={timeTrendData} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis domain={[-100, 100]} />
                      <Tooltip />
                      <Bar dataKey="score" name="NPS Score" fill="#5B21B6" />
                      <Legend verticalAlign="top" height={36} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            <Card className="col-span-1">
              <CardHeader>
                <CardTitle>NPS by Location</CardTitle>
                <CardDescription>Comparison across different branches</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={locationTrendData} layout="vertical" margin={{ top: 20, right: 30, left: 100, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[-100, 100]} />
                      <YAxis dataKey="name" type="category" />
                      <Tooltip />
                      <Bar dataKey="score" name="NPS Score" fill="#5B21B6" />
                      <Legend verticalAlign="top" height={36} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="responses">
          <Card>
            <CardHeader>
              <CardTitle>Individual Responses</CardTitle>
              <CardDescription>All NPS survey responses</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <table className="min-w-full divide-y divide-border">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Patient</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Score</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Feedback</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-background">
                    {isLoading ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-3 text-center">Loading data...</td>
                      </tr>
                    ) : (
                      patientCalls?.filter(call => call.score !== null)
                        .sort((a, b) => new Date(b.lastVisitDate).getTime() - new Date(a.lastVisitDate).getTime())
                        .slice(0, 10)
                        .map((call) => (
                        <tr key={call.id}>
                          <td className="px-4 py-3 text-sm">{new Date(call.lastVisitDate).toLocaleDateString()}</td>
                          <td className="px-4 py-3 text-sm">{call.patientName}</td>
                          <td className="px-4 py-3 text-sm">{call.locationName}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={getNPSScoreClass(call.score!)}>{call.score}</span>
                          </td>
                          <td className="px-4 py-3 text-sm max-w-md truncate">{call.reviewContent || "-"}</td>
                          <td className="px-4 py-3 text-sm">
                            <Button variant="outline" size="sm">View</Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="callcampaign">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <Card className="col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Call Campaign Status</CardTitle>
                <CardDescription>Random Patient Survey Calls</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center h-32">
                  <div className="text-5xl font-bold mb-2">{getCallTarget()}</div>
                  <div className="text-sm text-muted-foreground mb-4">Target patients per month</div>
                  <Button className="w-full">Generate New List</Button>
                </div>
              </CardContent>
            </Card>

            <Card className="col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Gift Card Budget</CardTitle>
                <CardDescription>$20 incentive per completed survey</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center h-32">
                  <div className="text-5xl font-bold mb-2">${getCallTarget() * 20}</div>
                  <div className="text-sm text-muted-foreground mb-4">Monthly budget</div>
                  <Progress value={30} className="h-2 w-full" />
                  <div className="text-xs text-muted-foreground mt-2 w-full text-right">
                    ${(patientCalls?.filter(call => call.callStatus === 'Completed').length || 0) * 20} used this month
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Campaign Performance</CardTitle>
                <CardDescription>Call response rate</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center h-32">
                  <div className="text-5xl font-bold mb-2">
                    {patientCalls?.length 
                      ? Math.round((patientCalls.filter(call => call.callStatus === 'Completed').length / patientCalls.length) * 100)
                      : 0}%
                  </div>
                  <div className="text-sm text-muted-foreground mb-4">Response rate</div>
                  <div className="flex gap-4 text-sm">
                    <div className="flex flex-col items-center">
                      <span className="font-semibold">
                        {patientCalls?.filter(call => call.callStatus === 'Completed').length || 0}
                      </span>
                      <span className="text-xs text-muted-foreground">Completed</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="font-semibold">
                        {patientCalls?.filter(call => call.callStatus === 'Pending').length || 0}
                      </span>
                      <span className="text-xs text-muted-foreground">Pending</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="font-semibold">
                        {patientCalls?.filter(call => call.callStatus === 'No Answer').length || 0}
                      </span>
                      <span className="text-xs text-muted-foreground">No Answer</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex flex-wrap gap-3 justify-between items-center">
                <div>
                  <CardTitle>Patient Call List</CardTitle>
                  <CardDescription>
                    Random patients selected for NPS calls with $20 gift card incentive
                    {selectedLocationIds.length > 1 && !selectedLocationIds.includes('all') && (
                      <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                        {selectedLocationIds.map(id => getLocationName(id)).join(' + ')}
                      </span>
                    )}
                  </CardDescription>
                </div>
                <Button>Send Gift Card</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <table className="min-w-full divide-y divide-border">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Patient Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Last Visit</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Score</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Asked for Review</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Review Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Gift Card</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-background">
                    {isLoading ? (
                      <tr>
                        <td colSpan={10} className="px-4 py-3 text-center">Loading data...</td>
                      </tr>
                    ) : (
                      patientCalls?.map((call) => (
                        <tr key={call.id}>
                          <td className="px-4 py-3 text-sm">{call.patientName}</td>
                          <td className="px-4 py-3 text-sm">{call.phone}</td>
                          <td className="px-4 py-3 text-sm">{call.locationName}</td>
                          <td className="px-4 py-3 text-sm">{new Date(call.lastVisitDate).toLocaleDateString()}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              call.callStatus === 'Completed' ? 'bg-green-100 text-green-800' :
                              call.callStatus === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {call.callStatus}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {call.score !== null ? (
                              <span className={getNPSScoreClass(call.score)}>{call.score}</span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <input 
                              type="checkbox" 
                              checked={call.requestedReview} 
                              readOnly
                              className="h-4 w-4 rounded border-gray-300"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {call.reviewCompleted ? (
                              <span className="text-green-600">✓ Completed</span>
                            ) : call.reviewLinkSent ? (
                              <span className="text-yellow-600">Link Sent</span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {call.callStatus === 'Completed' ? (
                              <span className="text-green-600">✓ Sent</span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <Button variant="outline" size="sm">Edit</Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="googlereviews">
          <Card>
            <CardHeader>
              <CardTitle>Google Reviews</CardTitle>
              <CardDescription>
                Patient reviews from Google linked to NPS surveys
                {selectedLocationIds.length > 1 && !selectedLocationIds.includes('all') && (
                  <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                    {selectedLocationIds.map(id => getLocationName(id)).join(' + ')}
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <table className="min-w-full divide-y divide-border">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Patient</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Rating</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Review</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Linked to NPS</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Response</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-background">
                    {isLoading ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-3 text-center">Loading data...</td>
                      </tr>
                    ) : (
                      googleReviews?.map((review) => (
                        <tr key={review.id}>
                          <td className="px-4 py-3 text-sm">{new Date(review.reviewDate).toLocaleDateString()}</td>
                          <td className="px-4 py-3 text-sm">{review.patientName}</td>
                          <td className="px-4 py-3 text-sm">{getLocationName(review.locationId)}</td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center">
                              {'⭐'.repeat(review.rating)}
                              <span className="ml-1 text-xs">({review.rating})</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm max-w-md truncate">{review.reviewText}</td>
                          <td className="px-4 py-3 text-sm">
                            {review.linkedToNPS ? (
                              <span className="text-green-600">✓ Yes</span>
                            ) : (
                              <span className="text-gray-500">No</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {review.responseSent ? (
                              <span className="text-green-600">✓ Sent</span>
                            ) : (
                              <span className="text-yellow-600">Pending</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <Button variant="outline" size="sm">Respond</Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}